// src/App.js
import React, { lazy, Suspense, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { catalogDb } from "./firebase";
import { CartProvider } from "./context/CartContext";
import SiteLayout from "./components/SiteLayout";
import LoadingScreen from "./components/LoadingScreen";
import { UserProvider } from "./context/UserContext";
import { PlayerProvider } from "./context/PlayerContext";
import { VibeModeProvider } from "./context/VibeModeContext";
import { ModalProvider } from "./context/ModalContext";
import useButtonSound from "./hooks/useButtonSound";
import useAutoScale from "./hooks/useAutoScale";
import PrivateRoute from "./components/PrivateRoute";
import smokeGIF from "./images/logo/Smoke.gif";
import "./components/CustomStyles.css";
import "./styles/vtk-ai-agent.css";
import useTracks from "./hooks/useTracks";
import { isAdminExperiencePath } from "./adminShell/adminRouteConfig";
import { useGlobalLoading } from "./context/LoadingProvider";
import AnalyticsRouteObserver from "./components/AnalyticsRouteObserver";
import {PUBLIC_ROUTE_ALIASES} from "./lib/publicRouteAliases";
import * as runtimeConfig from "./lib/runtimeConfig";

const MusicStore = lazy(() => import("./components/MusicStore"));
const StorefrontPage = lazy(() => import("./pages/StorefrontPage"));
const BeatDetailPage = lazy(() => import("./pages/BeatDetailPage"));
const Success = lazy(() => import("./pages/Success"));
const MixRequestPortal = lazy(() => import("./pages/MixRequestPortal"));
const GlobalAudioPlayer = lazy(() => import("./components/GlobalAudioPlayer"));
const VibeMode = lazy(() => import("./components/VibeMode"));
const Login = lazy(() => import("./components/login"));
const MixUploadPage = lazy(() => import("./pages/MixUploadPage"));
const Cancel = lazy(() => import("./pages/Cancel.jsx"));
const RecordingVerify = lazy(() => import("./pages/RecordingVerify.jsx"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Cart = lazy(() => import("./components/Cart"));
const OutreachBeatsPage = lazy(() => import("./pages/OutreachBeatsPage.jsx"));
const CustomBeatsPortal = lazy(() => import("./pages/CustomBeatsPortal"));
const ModalHub = lazy(() => import("./components/ModalHub"));
const TollFreeOptInPage = lazy(() => import("./pages/TollFreeOptInPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsAndConditionsPage = lazy(() => import("./pages/TermsAndConditionsPage"));
const SupportContactPage = lazy(() => import("./pages/SupportContactPage"));
const UserDataDeletionPage = lazy(() => import("./pages/UserDataDeletionPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const PortalWebPay = lazy(() => import("./pages/PortalWebPay"));
const TikTokVideoShortsPanel = lazy(() => import("./components/admin/TikTokVideoShortsPanel.jsx"));

const IGOauthCallback = lazy(() => import("./components/admin/review/IGOauthCallback.jsx"));
const InstagramAnalyticsPanel = lazy(() => import("./admin/ai-dashboard/InstagramAnalyticsPanel"));
const PrintfulStorefront = lazy(() => import("./pages/PrintfulStorefront"));
const OrderStatus = lazy(() => import("./pages/OrderStatus"));
const AdminShell = lazy(() => import("./adminShell/AdminShell"));
const ProductionContentStudioShell = lazy(() => import("./adminShell/ProductionContentStudioShell"));
const AdminContentStudioPage = lazy(() => import("./adminShell/AdminContentStudioPageV2").then((module) => ({default: module.AdminContentStudioPage})));
const ProductionContentStudioHome = lazy(() => import("./adminShell/ProductionContentStudioShell").then((module) => ({default: module.ProductionContentStudioHome})));
const AdminOverview = lazy(() => import("./adminShell/AdminOverview"));
const AdminParityClosurePages = lazy(() => import("./adminShell/AdminParityClosurePages"));
const AdminSystemPages = lazy(() => import("./adminShell/AdminSystemPages"));
const AdminMarketingPages = lazy(() => import("./adminShell/AdminMarketingPages"));
const AdminMarketingOutreachPage = lazy(() => import("./adminShell/AdminMarketingProspectIntelligencePage"));
const AdminMarketingOutreachReviewPage = lazy(() => import("./adminShell/AdminMarketingOutreachReviewPage"));
const AdminBatchAAcceptancePage = lazy(() => import("./adminShell/AdminBatchAAcceptancePage"));
const AdminSocialPages = lazy(() => import("./adminShell/AdminSocialPages"));
const AdminNotFoundPage = lazy(() => import("./adminShell/AdminNotFoundPage"));
const AdminLegacyHost = lazy(() => import("./adminShell/AdminLegacyHost"));
const AdminLegacyContent = lazy(() => import("./adminShell/AdminLegacyContent"));
const AdminTrackMetadataPage = lazy(() => import("./adminShell/AdminTrackMetadataPage"));
const AdminBeatMatchUsagePage = lazy(() => import("./adminShell/AdminBeatMatchUsagePage"));
const BeatMatchBetaPage = lazy(() => import("./adminShell/BeatMatchBetaPage"));
const BeatMatchMediaPilotPage = lazy(() => import("./adminShell/BeatMatchMediaPilotPage"));
const AdminPhaseOnePages = lazy(() => import("./adminShell/AdminPhaseOnePageRouter"));
const AdminYouTubeThumbnailPage = lazy(() => import("./adminShell/AdminYouTubeThumbnailPage"));
const AdminPublishingOperationsPage = lazy(() => import("./adminShell/AdminWaveThreePages").then((module) => ({default: module.AdminPublishingOperationsPage})));
const AdminSocialConnectionsPage = lazy(() => import("./adminShell/AdminWaveThreePages").then((module) => ({default: module.AdminSocialConnectionsPage})));
const AdminAutomationOperationsPage = lazy(() => import("./adminShell/AdminWaveThreePages").then((module) => ({default: module.AdminAutomationOperationsPage})));
const AdminCreativeMigrationPage = lazy(() => import("./adminShell/AdminWaveThreePages").then((module) => ({default: module.AdminCreativeMigrationPage})));
const AdminInstagramOperationsPage = lazy(() => import("./adminShell/AdminWaveThreeMutationPages").then((module) => ({default: module.AdminInstagramOperationsPage})));
const AdminXAgentOperations = lazy(() => import("./adminShell/AdminWaveThreeMutationPages").then((module) => ({default: module.AdminXAgentOperations})));
const AdminCreativeStudioModePage = lazy(() => import("./adminShell/AdminWaveThreeMutationPages").then((module) => ({default: module.AdminCreativeStudioModePage})));
const AdminTelegramMediaOverridePage = lazy(() => import("./adminShell/AdminWaveThreeMutationPages").then((module) => ({default: module.AdminTelegramMediaOverridePage})));
const AdminOrdersPage = lazy(() => import("./adminShell/AdminCommerceServicesPages").then((module) => ({default: module.AdminOrdersPage})));
const AdminOrderPage = lazy(() => import("./adminShell/AdminCommerceServicesPages").then((module) => ({default: module.AdminOrderPage})));
const AdminAgreementsPage = lazy(() => import("./adminShell/AdminPassCPages").then((module) => ({default: module.AdminAgreementsPageV2})));
const AdminFulfillmentPageV2 = lazy(() => import("./adminShell/AdminCommerceServicesPages").then((module) => ({default: module.AdminFulfillmentPageV2})));
const AdminRecordingPage = lazy(() => import("./adminShell/AdminPassCPages").then((module) => ({default: module.AdminRecordingPageV2})));
const AdminSubscribersPage = lazy(() => import("./adminShell/AdminPassCPages").then((module) => ({default: module.AdminSubscribersPage})));
const AdminMixingPage = lazy(() => import("./adminShell/AdminCommerceServicesPages").then((module) => ({default: module.AdminMixingPage})));
const AdminCustomBeatsPage = lazy(() => import("./adminShell/AdminCommerceServicesPages").then((module) => ({default: module.AdminCustomBeatsPage})));
const contentStudioSurface = runtimeConfig.buildRuntimeConfig(process.env).contentStudioSurfaceMode;
const narrowProductionAdminSurface = runtimeConfig.buildRuntimeConfig(process.env).deploymentEnvironment === "production";
const storefrontMediaPresentation = runtimeConfig.buildRuntimeConfig(process.env).productionStorefrontMediaPresentation;

function PreserveQueryRedirect({to}) { const location = useLocation(); return <Navigate to={`${to}${location.search || ""}`} replace />; }

class RouteErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = {error: null}; }
  static getDerivedStateFromError(error) { return {error}; }
  componentDidUpdate(previous) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) this.setState({error: null});
  }
  render() {
    if (!this.state.error) return this.props.children;
    return <main role="alert" style={{padding: 32, minHeight: "100vh", background: "#130718", color: "white"}}>
      <h1>This page could not be loaded.</h1><p>The route stopped safely instead of leaving the loading screen active.</p>
      <button type="button" onClick={() => window.location.reload()}>Retry</button>
    </main>;
  }
}

function RouteBoundary() {
  const location = useLocation();
  return <RouteErrorBoundary resetKey={`${location.pathname}${location.search}`}><AppContent /></RouteErrorBoundary>;
}

function ParityPage({name}) {
  return <AdminParityClosurePages page={name} />;
}

/* ──────────────────────────────────────────────────────────────
   One-shot helper: if “skipLoader” was set by the **softRefresh**
   util, skip the splash **once** and immediately clear the flag.
   ──────────────────────────────────────────────────────────── */
function useMaybeSkipSplash() {
  return React.useMemo(() => {
    const shouldSkip = sessionStorage.getItem("skipLoader") === "1";
    if (shouldSkip) sessionStorage.removeItem("skipLoader");
    return shouldSkip;
  }, []);
}

/* ──────────────────────────────────────────────────────────────
   Route switch for /beats:
   - if ?outreachFilter=… → OutreachBeatsPage (curated pack)
   - otherwise           → MusicStore (normal beats page)
   ──────────────────────────────────────────────────────────── */
function BeatsRouteSwitch() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const outreachRaw = (params.get("outreachFilter") || "").trim();
  const hasOutreachFilter = outreachRaw.length > 0;

  if (hasOutreachFilter) return <OutreachBeatsPage />;
  return <MusicStore />;
}

function SharedTrackLoadingScreen({onFinish}) {
  const tracks = useTracks(catalogDb);
  return <LoadingScreen tracks={tracks} onFinish={onFinish} />;
}

/* ──────────────────────────────────────────────────────────────
   Decide which routes should NOT be trapped inside the fixed canvas
   (these pages need real scrolling + no splash overlay interference)
   ──────────────────────────────────────────────────────────── */
function isFullPageRoute(pathname) {
  const p = String(pathname || "");
  return (
    p === "/" ||
    p === "/catalog" ||
    p.startsWith("/collections/") ||
    p.startsWith("/beats/") ||
    p === "/cart" ||
    p.startsWith("/checkout") ||
    p.startsWith("/pay") ||
    p === "/success" ||
    p === "/cancel" ||
    p === "/printful" ||
    p.startsWith("/order") ||
    p === "/tollfree-opt-in" ||
    p === "/privacy-policy" ||
    p === "/terms" ||
    p === "/support" ||
    p.startsWith("/recording") ||
    p === "/beatmatch-beta" ||
    p === "/beatmatch-media-pilot" ||
    p.startsWith("/admin/tg-image-picker") ||
    isAdminExperiencePath(p)
  );
}

/* ──────────────────────────────────────────────────────────────
   Routes that should never show the splash while navigating
   between admin tools
   ──────────────────────────────────────────────────────────── */
function isNoSplashRoute(pathname) {
  const p = String(pathname || "");
  return isAdminExperiencePath(p);
}

function AppContent() {
  const location = useLocation();
  const {reset: resetGlobalLoading} = useGlobalLoading();
  const firstSkipSplash = useMaybeSkipSplash();

  const fullPage = isFullPageRoute(location.pathname);
  const skipSplash = firstSkipSplash || fullPage || isNoSplashRoute(location.pathname);

  // splash state
  const [doneLoading, setDoneLoading] = useState(skipSplash);

  useEffect(() => {
    resetGlobalLoading();
  }, [location.key, location.pathname, location.search, resetGlobalLoading]);

  // niceties
  useButtonSound();
  useAutoScale(1024, 1024);

  // If user hits checkout/success/payment routes directly, never block with splash
  useEffect(() => {
    if (fullPage || isNoSplashRoute(location.pathname)) setDoneLoading(true);
  }, [fullPage, location.pathname]);

  // BODY background fade-in
  useEffect(() => {
    document.body.classList.add("bg-fade");
    document.body.classList.toggle("show", doneLoading);
  }, [doneLoading]);

  // For checkout/payment/success/legal pages, use a normal scrolling container
  const appContentStyle = fullPage
    ? {
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }
    : {
        position: "fixed",
        inset: 0,
        overflow: "hidden",
      };

  // On payment/success pages, it’s safer to hide the floating cart + modals
  const showFloatingCartAndModals = !fullPage;

  const suspenseFallback = (
    <div
      aria-busy="true"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        backgroundImage: `url(${smokeGIF})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    />
  );

  return (
    <PlayerProvider>
      <VibeModeProvider enabled={storefrontMediaPresentation}>
      <div id="app-content" style={appContentStyle}>
        <SiteLayout>
          <UserProvider>
            <CartProvider>
              <ModalProvider>
                <Suspense fallback={suspenseFallback}>
                  <Routes location={location}>
                    <Route
                      path="/login"
                      element={
                        <Login
                          onSuccess={() => {
                            const returnTo = new URLSearchParams(window.location.search).get("returnTo");
                            window.location = returnTo?.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/aid";
                          }}
                        />
                      }
                    />

                    {/* Search-friendly storefront; the immersive player remains at /beats. */}
                    <Route path="/" element={<StorefrontPage page="home" />} />
                    <Route path="/catalog" element={<StorefrontPage page="catalog" />} />
                    <Route path="/collections/:collectionSlug" element={<StorefrontPage page="collection" />} />

                    {/* /beats now smart-switches between outreach + normal */}
                    <Route path="/beats" element={<BeatsRouteSwitch />} />
                    <Route path="/beats/:beatSegment/:slug?" element={<BeatDetailPage />} />
                    <Route path="/mixing" element={<Navigate to={PUBLIC_ROUTE_ALIASES["/mixing"]} replace />} />
                    <Route path="/custom-beats" element={<Navigate to={PUBLIC_ROUTE_ALIASES["/custom-beats"]} replace />} />
                    <Route path="/recording" element={<Navigate to={PUBLIC_ROUTE_ALIASES["/recording"]} replace />} />
                    <Route path="/printful" element={<PrintfulStorefront />} />
                    <Route path="/order/:id" element={<OrderStatus />} />
                    <Route path="/order" element={<OrderStatus />} />

                    {/* ✅ FIX: ensure leading slash */}
                    <Route path="/recording/verify" element={<RecordingVerify />} />
                    <Route path="/beatmatch-beta" element={<BeatMatchBetaPage />} />
                    <Route path="/beatmatch-media-pilot" element={<BeatMatchMediaPilotPage />} />

                    {/* Production is deliberately narrowed to the explicitly enabled Content Studio core. */}
                    {narrowProductionAdminSurface ? <Route path="/aid" element={<PrivateRoute><ProductionContentStudioShell /></PrivateRoute>}>
                      <Route index element={<ProductionContentStudioHome enabled={contentStudioSurface === "production-core"} />} />
                      {contentStudioSurface === "production-core" ? <Route path="marketing/content-studio" element={<AdminContentStudioPage productionCore />} /> : null}
                      <Route path="music/track-metadata" element={<AdminTrackMetadataPage />} />
                      <Route path="music/beatmatch-usage" element={<AdminBeatMatchUsagePage />} />
                      <Route path="*" element={<AdminNotFoundPage />} />
                    </Route> : <Route path="/aid" element={<PrivateRoute><AdminShell /></PrivateRoute>}>
                      <Route index element={<AdminOverview />} />
                      <Route path="commerce/orders" element={<AdminOrdersPage />} />
                      <Route path="commerce/orders/:orderId" element={<AdminOrderPage />} />
                      <Route path="commerce/agreements" element={<AdminAgreementsPage />} />
                      <Route path="services/mixing" element={<AdminMixingPage />} />
                      <Route path="services/custom-beats" element={<AdminCustomBeatsPage />} />
                      <Route path="marketing/outreach" element={<AdminMarketingOutreachPage />} />
                      <Route path="marketing/outreach-review" element={<AdminMarketingOutreachReviewPage />} />
                      <Route path="marketing/outreach/batch-a" element={<AdminBatchAAcceptancePage />} />
                      <Route path="marketing" element={<AdminMarketingPages />} />
                      <Route path="social" element={<AdminSocialPages />} />
                      <Route path="social/instagram" element={<AdminSocialPages />} />
                      <Route path="social/instagram/drafts" element={<AdminInstagramOperationsPage />} />
                      <Route path="social/instagram/inbox" element={<AdminInstagramOperationsPage />} />
                      <Route path="social/instagram/moderation" element={<PreserveQueryRedirect to="/aid/social/instagram/inbox" />} />
                      <Route path="social/instagram/analytics" element={<AdminLegacyContent><InstagramAnalyticsPanel /></AdminLegacyContent>} />
                      <Route path="social/instagram/videos" element={<AdminInstagramOperationsPage />} />
                      <Route path="social/tiktok" element={<AdminSocialPages />} />
                      <Route path="marketing/canary" element={<AdminMarketingPages />} />
                      <Route path="commerce/fulfillment" element={<AdminFulfillmentPageV2 />} />
                      <Route path="music/beats" element={<AdminPhaseOnePages />} />
                      <Route path="music/beats/fix-track-images" element={<Navigate to="/aid/music/beats?tab=image-upload&mode=repair" replace />} />
                      <Route path="music/track-metadata" element={<AdminTrackMetadataPage />} />
                      <Route path="music/beatmatch-usage" element={<AdminBeatMatchUsagePage />} />
                      <Route path="music/beats/pro-tools-stems" element={<Navigate to="/aid/music/beats?tab=pro-tools-stems" replace />} />
                      <Route path="music/beats/:trackId" element={<ParityPage name="AdminBeatDetailRedirect" />} />
                      <Route path="music/beat-packs" element={<AdminPhaseOnePages />} />
                      <Route path="music/audio-analysis" element={<AdminPhaseOnePages />} />
                      <Route path="music/media-library" element={<AdminPhaseOnePages />} />
                      <Route path="music/tag-bank" element={<AdminPhaseOnePages />} />
                      <Route path="services/recording" element={<AdminRecordingPage />} />
                      <Route path="services/recording-sessions" element={<AdminRecordingPage />} />
                      <Route path="marketing/content" element={<AdminMarketingPages />} />
                      <Route path="marketing/content-studio" element={<AdminPhaseOnePages />} />
                      <Route path="marketing/review" element={<AdminMarketingPages />} />
                      <Route path="marketing/queue" element={<AdminMarketingPages />} />
                      <Route path="marketing/publishing" element={<AdminPublishingOperationsPage />} />
                      <Route path="marketing/approvals" element={<AdminMarketingPages />} />
                      <Route path="marketing/campaigns" element={<AdminMarketingPages />} />
                      <Route path="marketing/analytics" element={<AdminPhaseOnePages />} />
                      <Route path="marketing/experiments" element={<AdminMarketingPages />} />
                      <Route path="marketing/attribution" element={<AdminMarketingPages />} />
                      <Route path="social/x" element={<AdminSocialPages />} />
                      <Route path="social/x/operations" element={<AdminXAgentOperations />} />
                      <Route path="social/connections" element={<AdminSocialConnectionsPage />} />
                      <Route path="social/youtube" element={<AdminSocialPages />} />
                      <Route path="media/library" element={<Navigate to="/aid/music/media-library" replace />} />
                      <Route path="media/mutations" element={<PreserveQueryRedirect to="/aid/music/media-library" />} />
                      <Route path="media/live-video" element={<AdminCreativeStudioModePage mode="live-video" />} />
                      <Route path="media/instagram-video" element={<AdminCreativeStudioModePage mode="instagram" />} />
                      <Route path="media/creative-migration" element={<AdminCreativeMigrationPage />} />
                      <Route path="media/youtube-thumbnail" element={<AdminYouTubeThumbnailPage />} />
                      <Route path="youtube-thumbnail" element={<PreserveQueryRedirect to="/aid/media/youtube-thumbnail" />} />
                      <Route path="communications/purchases" element={<ParityPage name="AdminPurchasesAlias" />} />
                      <Route path="communications/subscribers" element={<AdminSubscribersPage />} />
                      <Route path="communications/email" element={<Navigate to="/aid/operations/email" replace />} />
                      <Route path="operations/email" element={<AdminPhaseOnePages />} />
                      <Route path="communications/telegram" element={<AdminTelegramMediaOverridePage />} />
                      <Route path="customers/purchases" element={<ParityPage name="AdminPurchasesAlias" />} />
                      <Route path="customers/subscribers" element={<AdminSubscribersPage />} />
                      <Route path="system" element={<AdminSystemPages />} />
                      <Route path="system/integrations" element={<AdminSystemPages />} />
                      <Route path="system/schedulers" element={<AdminSystemPages />} />
                      <Route path="system/automation" element={<AdminAutomationOperationsPage />} />
                      <Route path="system/safety" element={<AdminSystemPages />} />
                      <Route path="system/appearance" element={<Navigate to="/aid/site/appearance" replace />} />
                      <Route path="system/settings" element={<Navigate to="/aid/site/pricing-features" replace />} />
                      <Route path="site/pricing-features" element={<AdminPhaseOnePages />} />
                      <Route path="site/appearance" element={<AdminPhaseOnePages />} />
                      <Route path="site/parallax" element={<PreserveQueryRedirect to="/aid/site/appearance" />} />
                      <Route path="site/loading-screen" element={<PreserveQueryRedirect to="/aid/site/appearance" />} />
                      <Route path="system/diagnostics" element={<AdminSystemPages />} />
                      <Route path="system/environment" element={<AdminSystemPages />} />
                      <Route path="legacy/admin" element={<PreserveQueryRedirect to="/aid" />} />
                      <Route path="*" element={<AdminNotFoundPage />} />
                    </Route>}

                    {/* Canonical Admin is /aid. Historical Admin routes redirect to their native destinations. */}
                    <Route path="/admin" element={<Navigate to="/aid" replace />} />
                    <Route path="/videos" element={<ParityPage name="InstagramVideosLegacyRedirect" />} />
                    <Route path="/igstats" element={<ParityPage name="InstagramAnalyticsLegacyRedirect" />} />
                    <Route path="/ig-agent" element={<ParityPage name="InstagramAgentLegacyRedirect" />} />

                    <Route path="/custom-portal" element={<CustomBeatsPortal mode="user" />} />
                    <Route path="/custom-portal/admin" element={<ParityPage name="CustomBeatsLegacyRedirect" />} />
                    <Route path="/admin/ig-app-review" element={<PreserveQueryRedirect to="/aid/social/instagram/drafts" />} />
                    <Route path="/admin/ig-scanner" element={<PreserveQueryRedirect to="/aid/marketing/outreach" />} />
                    <Route path="/ig-review/content-publish" element={<PreserveQueryRedirect to="/aid/social/instagram/drafts" />} />
                    <Route path="/ig-review/manage-comments" element={<PreserveQueryRedirect to="/aid/social/instagram/inbox" />} />
                    <Route path="/ig-review/manage-insights" element={<PreserveQueryRedirect to="/aid/social/instagram/analytics" />} />
                    <Route path="/admin/ig-review/oauth-callback" element={<AdminLegacyHost title="Instagram OAuth Callback" description="Existing authenticated OAuth exchange result." section="System" legacyPath="/admin/ig-review/oauth-callback"><IGOauthCallback /></AdminLegacyHost>} />
                    <Route path="/admin/tiktok" element={<AdminLegacyHost title="TikTok" description="Existing TikTok connection and posting workflow." section="Social Platforms" legacyPath="/admin/tiktok"><TikTokVideoShortsPanel /></AdminLegacyHost>} />
                    <Route path="/admin/tg-image-picker" element={<PreserveQueryRedirect to="/aid/communications/telegram" />} />



                    {/* Checkout routes */}
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/checkout/web" element={<CheckoutPage />} />

                    {/* ✅ IMPORTANT: alias for your Cloud Function default web-pay path */}
                    <Route path="/portal-web-pay" element={<CheckoutPage />} />

                    {/* Success / cancel */}
                    <Route path="/success" element={<Success />} />
                    <Route path="/cancel" element={<Cancel />} />
                    <Route path="/pay/success" element={<CheckoutSuccess />} />

                    {/* Mix routes */}
                    <Route path="/mix-upload" element={<MixUploadPage />} />
                    <Route path="/mix-portal" element={<MixRequestPortal />} />
                    <Route path="/mix-portal/admin" element={<ParityPage name="MixingLegacyRedirect" />} />

                    {/* Existing web-pay page (kept) */}
                    <Route path="/pay/web" element={<PortalWebPay />} />

                    {/* Legal / toll-free routes */}
                    <Route path="/tollfree-opt-in" element={<TollFreeOptInPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsAndConditionsPage />} />
                    <Route path="/data-deletion" element={<UserDataDeletionPage />} />
                    <Route path="/support" element={<SupportContactPage />} />

                    <Route path="/admin/purchases" element={<ParityPage name="PurchasesLegacyRedirect" />} />
                  </Routes>
                </Suspense>

                {/* Floating cart and modals are hidden on full-page flows. */}
                {showFloatingCartAndModals && (
                  <Suspense fallback={null}>
                    <Cart />
                    <ModalHub />
                  </Suspense>
                )}
              </ModalProvider>
            </CartProvider>
          </UserProvider>
        </SiteLayout>
      </div>

      {/* ─── one-time splash ─── */}
      {!doneLoading && (
        <div
          className="splash-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SharedTrackLoadingScreen onFinish={() => setDoneLoading(true)} />
          <Suspense fallback={null}>
            <GlobalAudioPlayer />
          </Suspense>
        </div>
      )}
      {storefrontMediaPresentation ? <Suspense fallback={null}><VibeMode /></Suspense> : null}
      </VibeModeProvider>
    </PlayerProvider>
  );
}

export default function App() {
  return (
    <Router>
      <AnalyticsRouteObserver />
      <RouteBoundary />
    </Router>
  );
}
